import {LotProperties} from 'notebook/integrations/chemistry/custom-evals/LotPropertiesCustomEval';
import {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  StringResolutionType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  coerceToPrimitiveNumber,
  isTypedValueEmpty,
  isUnitReferenceTypedValue,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {UnitReferenceTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';

const PURITY_RANGE_ERROR = 'Purity must be a number between 0 and 100%.';
const DEFAULT_PURITY = '100';

/** Relative tolerance for floating-point comparison (1e-9). */
function isApproximatelyEqualRelative(a: number, b: number): boolean {
  if (a === b) {
    return true;
  }
  const diff = Math.abs(a - b);
  const largest = Math.max(Math.abs(a), Math.abs(b));
  return diff <= largest * 1e-9;
}

/**
 * Resolves lot properties from the shared resolution cache.
 * Returns {properties, loading} — loading is true while the resolution is in-flight.
 */
function resolveLotProperties(
  lotCellValue: TypedValue | null,
  resolveFromResolutionQuery: ResolveQueryFn
): {properties: LotProperties | null; loading: boolean} {
  if (lotCellValue == null || lotCellValue.type !== BaseTypedValueType.BIOENTITY) {
    return {properties: null, loading: false};
  }

  const lotId = lotCellValue.bioentitySummary.id;
  const {resolvedValue, error: resolutionError} = resolveFromResolutionQuery(
    lotCellValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_LOT_PROPERTIES,
      lotId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  // If resolution errored, check whether it's still loading or a real error.
  // Real errors (e.g. "not a molecule lot") mean no properties are available.
  if (resolutionError != null) {
    const isLoading = (resolutionError as {type?: string}).type === ExcessUtil.LoadingError.type;
    return {properties: null, loading: isLoading};
  }

  if (resolvedValue == null || resolvedValue.type !== BaseTypedValueType.JSON) {
    return {properties: null, loading: false};
  }

  return {properties: resolvedValue.jsonValue as LotProperties, loading: false};
}

/**
 * Gets the lot's concentration value and unit symbol.
 * Returns null if either value or unit is missing (both are required for auto-fill).
 * Prefers molar concentration; falls back to density.
 */
function getLotConcentration(
  lotProperties: LotProperties
): {value: string; unitId: string; unitSymbol: string} | null {
  if (
    lotProperties.concentrationMolar != null &&
    lotProperties.concentrationMolarUnitId != null &&
    lotProperties.concentrationMolarUnitSymbol != null
  ) {
    return {
      value: lotProperties.concentrationMolar,
      unitId: lotProperties.concentrationMolarUnitId,
      unitSymbol: lotProperties.concentrationMolarUnitSymbol,
    };
  }
  if (
    lotProperties.concentrationDensity != null &&
    lotProperties.concentrationDensityUnitId != null &&
    lotProperties.concentrationDensityUnitSymbol != null
  ) {
    return {
      value: lotProperties.concentrationDensity,
      unitId: lotProperties.concentrationDensityUnitId,
      unitSymbol: lotProperties.concentrationDensityUnitSymbol,
    };
  }
  return null;
}

export enum LotConcentrationStatus {
  LOADING = 'LOADING',
  HAS_CONCENTRATION = 'HAS_CONCENTRATION',
  /** No lot, a lot without a concentration, or lot properties that could not be resolved. */
  NO_CONCENTRATION = 'NO_CONCENTRATION',
}

/**
 * Whether loaded lot properties have a concentration; null properties (no lot, or a lot whose properties
 * could not be resolved) have none. Shared by the Amount unit custom eval and the Amount unit picker so they
 * apply the same rule.
 */
export function getLotConcentrationStatusFromProperties(
  properties: LotProperties | null
): LotConcentrationStatus.HAS_CONCENTRATION | LotConcentrationStatus.NO_CONCENTRATION {
  return properties != null && getLotConcentration(properties) != null
    ? LotConcentrationStatus.HAS_CONCENTRATION
    : LotConcentrationStatus.NO_CONCENTRATION;
}

/** Whether the lot in `lotCellValue` has a concentration, resolved through `resolveFromResolutionQuery`. */
export function getLotConcentrationStatus(
  lotCellValue: TypedValue | null,
  resolveFromResolutionQuery: ResolveQueryFn
): LotConcentrationStatus {
  const {properties, loading} = resolveLotProperties(lotCellValue, resolveFromResolutionQuery);
  if (loading) {
    return LotConcentrationStatus.LOADING;
  }
  return getLotConcentrationStatusFromProperties(properties);
}

/**
 * Custom eval for the purity column that auto-fills from the lot's purity value.
 *
 * cellDependencies[0] = lot column's evaluated value (BIOENTITY)
 *
 * Behavior:
 * - Lot selected + has purity + cell empty → auto-fill with lot's purity
 * - No lot (or lot has no purity) + cell empty → default to 100%
 * - Cell has value → validate 0-100% range
 * - Lot loading → loading error
 */
export function getLotPurityCustomEval(
  typedValue: TypedValue,
  _columnDefinition: ColumnDefinition | null,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  const lotCellValue = cellDependencies[0];
  const {properties: lotProperties, loading} = resolveLotProperties(lotCellValue, resolveFromResolutionQuery);

  // If lot is selected but still loading, show loading error
  if (loading) {
    return {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false};
  }

  // If the cell is empty, auto-fill from lot or use default
  if (isTypedValueEmpty(typedValue)) {
    const lotPurity = lotProperties?.purity ?? null;
    const purityValue = lotPurity ?? DEFAULT_PURITY;
    const autoFilledValue = {
      type: BaseTypedValueType.STRING,
      rawValue: purityValue,
    } as TypedValue;
    // Lock the cell when auto-filled from lot (user must clear lot to edit)
    return {val: autoFilledValue, error: null, locked: lotPurity != null};
  }

  // Validate the value (manually entered)
  const numericValue = coerceToPrimitiveNumber(typedValue);
  if (numericValue == null || numericValue <= 0 || numericValue > 100) {
    return {val: typedValue, error: new ExcessUtil.ValueError(PURITY_RANGE_ERROR), locked: false};
  }

  // If lot has purity, compare exact PreciseDecimal values (not just numeric equality) so a
  // manually-entered value with different significant figures than the lot (e.g. "95.50" vs "95.5")
  // is also flagged as a mismatch, and warn if they don't match.
  const lotPurity = PreciseDecimal.ofOrUndefined(lotProperties?.purity);
  const manualPurity = PreciseDecimal.ofOrUndefined(
    typedValue.type === BaseTypedValueType.STRING ? typedValue.rawValue : undefined
  );
  if (lotPurity != null && (manualPurity == null || !manualPurity.equals(lotPurity))) {
    return {
      val: typedValue,
      error: new ExcessUtil.ValueError(`Purity does not match lot value (${lotProperties?.purity}%).`),
      locked: false,
    };
  }

  // Cell value matches lot purity exactly — lock it to keep cell non-editable after submission
  // (without this, submitting would make the auto-filled cell appear user-editable).
  if (lotPurity != null) {
    return {val: typedValue, error: null, locked: true};
  }

  return {val: typedValue, error: null, locked: false};
}

/**
 * Custom eval for the concentration VALUE column that auto-fills from the lot.
 *
 * cellDependencies layout (from getCustomEvalDependencies):
 *   [0] = CHEMISTRY_AMOUNT_UNIT
 *   [1] = CHEMISTRY_LOT (lot BIOENTITY)
 *
 * Behavior:
 * - Cell empty + lot has concentration → auto-fill value in the lot's native unit (the concentration
 *   unit is never user-configurable — see getLotConcentrationUnitValue — so no scaling is ever needed)
 * - Cell has value + lot has concentration → compare raw numbers, warn if different
 * - No lot or lot has no valid concentration → don't auto-fill (fall through)
 * - Lot loading → loading error
 */
export function getLotConcentrationValue(
  typedValue: TypedValue,
  cellDependencies: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): {autoFilled: boolean; result: CustomEvalResult} {
  const lotCellValue = cellDependencies[1] ?? null;
  const {properties: lotProperties, loading} = resolveLotProperties(lotCellValue, resolveFromResolutionQuery);

  // If lot is selected but still loading, show loading error
  if (loading) {
    return {
      autoFilled: true,
      result: {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false},
    };
  }

  // No lot or lot has no valid concentration → fall through to existing logic
  if (lotProperties == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  const lotConcentration = getLotConcentration(lotProperties);
  if (lotConcentration == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  // Cell is empty → auto-fill with the lot's concentration value (in the lot's own unit), preserving
  // the exact precision-preserving string from the lot (no scaling is ever needed since the
  // concentration unit is always locked to the lot's native unit)
  if (isTypedValueEmpty(typedValue)) {
    const autoFilledValue = {
      type: BaseTypedValueType.STRING,
      rawValue: lotConcentration.value,
    } as TypedValue;
    return {autoFilled: true, result: {val: autoFilledValue, error: null, locked: true}};
  }

  // Cell has a value → compare with the lot's value (use relative tolerance for floating point)
  const cellNumericValue = coerceToPrimitiveNumber(typedValue);
  const lotNumericValue = PreciseDecimal.ofOrUndefined(lotConcentration.value)?.toFloat();
  if (cellNumericValue != null && lotNumericValue == null) {
    // The lot's own concentration value isn't a parsable number (e.g. a malformed/legacy tag) — we
    // can't safely compare it against the cell, so surface an error rather than silently treating
    // the cell as matching.
    return {
      autoFilled: true,
      result: {
        val: typedValue,
        error: new ExcessUtil.ValueError(
          `Lot concentration value (${lotConcentration.value}) is not a valid number and could not be compared.`
        ),
        locked: false,
      },
    };
  }
  if (
    cellNumericValue != null &&
    lotNumericValue != null &&
    !isApproximatelyEqualRelative(cellNumericValue, lotNumericValue)
  ) {
    return {
      autoFilled: true,
      result: {
        val: typedValue,
        error: new ExcessUtil.ValueError(
          `Concentration does not match lot value (${lotConcentration.value} ${lotConcentration.unitSymbol}).`
        ),
        locked: false,
      },
    };
  }

  // Cell value matches lot → no warning needed
  return {autoFilled: true, result: {val: typedValue, error: null, locked: false}};
}

/**
 * Custom eval for the concentration UNIT column.
 *
 * The concentration unit is never user-configurable (see the decision doc linked from BNCH-162721):
 * whenever the lot has a concentration, this column always mirrors the lot's native unit. As with
 * the purity and concentration value columns, an existing cell value is never silently overwritten —
 * if it doesn't match the lot's unit, we show an error and leave the cell editable so the user can
 * clear it to accept the lot's unit.
 *
 * cellDependencies layout (from getCustomEvalDependencies):
 *   [0] = CHEMISTRY_AMOUNT_UNIT
 *   [1] = CHEMISTRY_LOT (lot BIOENTITY)
 *
 * Behavior:
 * - Cell empty + lot has concentration with unit → resolve and auto-fill the lot's unit, locked
 * - Cell has value + lot has concentration → compare unit ids, warn (and leave editable) if different
 * - No lot or lot has no valid concentration → don't auto-fill (fall through, cell stays editable)
 * - Lot loading or unit resolution pending → loading error
 */
export function getLotConcentrationUnitValue(
  typedValue: TypedValue,
  cellDependencies: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): {autoFilled: boolean; result: CustomEvalResult} {
  const lotCellValue = cellDependencies[1] ?? null;
  const {properties: lotProperties, loading: lotLoading} = resolveLotProperties(
    lotCellValue,
    resolveFromResolutionQuery
  );

  // If lot is selected but still loading properties, show loading error
  if (lotLoading) {
    return {
      autoFilled: true,
      result: {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false},
    };
  }

  // No lot or no valid concentration → fall through (cell remains freely editable)
  if (lotProperties == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  const lotConcentration = getLotConcentration(lotProperties);
  if (lotConcentration == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  // Cell already has a value — don't silently overwrite it (e.g. a value entered before a lot was
  // linked, or a stale value from before this behavior existed). Warn if it doesn't match the lot's
  // unit and leave the cell editable so the user can clear it to accept the lot's unit.
  if (!isTypedValueEmpty(typedValue)) {
    const matchesLotUnit =
      isUnitReferenceTypedValue(typedValue) && typedValue.unitSummary.id === lotConcentration.unitId;
    if (matchesLotUnit) {
      return {autoFilled: true, result: {val: typedValue, error: null, locked: true}};
    }
    return {
      autoFilled: true,
      result: {
        val: typedValue,
        error: new ExcessUtil.ValueError(
          `Concentration unit does not match the lot's unit (${lotConcentration.unitSymbol}). Clear the cell to use the lot's unit.`
        ),
        locked: false,
      },
    };
  }

  // Resolve the lot's unit symbol to a UnitReferenceTypedValue
  const {resolvedValue, error: unitResolutionError} = resolveFromResolutionQuery(
    toStringTypedValue(lotConcentration.unitSymbol),
    {
      string: lotConcentration.unitSymbol,
      type: ResolutionQueryType.STRING,
      valueType: {
        type: StringResolutionType.UNIT_REFERENCE,
      },
    },
    (resolved: UnitReferenceTypedValue): UnitReferenceTypedValue => resolved
  );

  // Unit resolution still loading
  if (unitResolutionError != null) {
    return {autoFilled: true, result: {val: typedValue, error: unitResolutionError, locked: false}};
  }

  return {autoFilled: true, result: {val: resolvedValue, error: null, locked: true}};
}
